import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@xxtract/ui';
import { useToast } from '@/components/feedback/Toaster';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { useSettings } from '../hooks/useSettings';
import { useUpdateSettings } from '../hooks/useUpdateSettings';
import type { SettingChange } from '../hooks/useUpdateSettings';

const STALENESS_MIN = 1;
const STALENESS_MAX = 90;
const LEADTIME_MIN = 1;
const LEADTIME_MAX = 1000;

export function SettingsPage() {
  const { t } = useTranslation();
  const { addToast } = useToast();
  const settingsQuery = useSettings();
  const updateSettings = useUpdateSettings();

  const [stalenessDays, setStalenessDays] = useState('');
  const [leadtimeMinObservations, setLeadtimeMinObservations] = useState('');
  const [auditNote, setAuditNote] = useState('');

  useEffect(() => {
    if (settingsQuery.data == null) return;
    setStalenessDays(String(settingsQuery.data.orderStalenessDays));
    setLeadtimeMinObservations(String(settingsQuery.data.leadtimeMinObservations));
  }, [settingsQuery.data]);

  if (settingsQuery.isError) {
    return <ApiErrorMessage error={settingsQuery.error} onRetry={() => settingsQuery.refetch()} />;
  }

  const stalenessNumber = Number(stalenessDays);
  const leadtimeNumber = Number(leadtimeMinObservations);
  const stalenessValid = Number.isInteger(stalenessNumber) && stalenessNumber >= STALENESS_MIN && stalenessNumber <= STALENESS_MAX;
  const leadtimeValid = Number.isInteger(leadtimeNumber) && leadtimeNumber >= LEADTIME_MIN && leadtimeNumber <= LEADTIME_MAX;
  const hasAuditNote = auditNote.trim().length > 0;

  const stalenessChanged = settingsQuery.data != null && stalenessNumber !== settingsQuery.data.orderStalenessDays;
  const leadtimeChanged = settingsQuery.data != null && leadtimeNumber !== settingsQuery.data.leadtimeMinObservations;
  const hasChanges = stalenessChanged || leadtimeChanged;

  const canSave = settingsQuery.data != null && stalenessValid && leadtimeValid && hasAuditNote && hasChanges && !updateSettings.isPending;

  function handleSave() {
    const changes: SettingChange[] = [];
    if (stalenessChanged) changes.push({ key: 'orderStalenessDays', value: stalenessNumber });
    if (leadtimeChanged) changes.push({ key: 'leadtimeMinObservations', value: leadtimeNumber });
    if (changes.length === 0) return;

    updateSettings.mutate({ changes, auditNote: auditNote.trim() }, {
      onSuccess: () => {
        addToast(t('admin.settings.saveSuccess'), 'success');
        setAuditNote('');
      },
      onError: () => addToast(t('admin.settings.saveError'), 'error'),
    });
  }

  return (
    <div className="max-w-2xl space-y-5" data-testid="settings-page">
      <header>
        <h1 className="text-xl font-bold tracking-tight text-[var(--foreground)]">{t('admin.settings.title')}</h1>
      </header>

      <Card data-testid="settings-process-section">
        <CardHeader>
          <CardTitle>{t('admin.settings.process.title')}</CardTitle>
          <CardDescription>{t('admin.settings.process.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--foreground)]">{t('admin.settings.process.stalenessLabel')}</span>
            <input
              aria-label={t('admin.settings.process.stalenessLabel')}
              className="h-9 w-40 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
              max={STALENESS_MAX}
              min={STALENESS_MIN}
              onChange={(event) => setStalenessDays(event.target.value)}
              type="number"
              value={stalenessDays}
            />
            {!stalenessValid && stalenessDays !== '' && (
              <span className="text-xs text-[var(--destructive)]">{t('admin.settings.process.stalenessInvalid', { min: STALENESS_MIN, max: STALENESS_MAX })}</span>
            )}
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--foreground)]">{t('admin.settings.process.leadtimeLabel')}</span>
            <input
              aria-label={t('admin.settings.process.leadtimeLabel')}
              className="h-9 w-40 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 text-sm"
              max={LEADTIME_MAX}
              min={LEADTIME_MIN}
              onChange={(event) => setLeadtimeMinObservations(event.target.value)}
              type="number"
              value={leadtimeMinObservations}
            />
            {!leadtimeValid && leadtimeMinObservations !== '' && (
              <span className="text-xs text-[var(--destructive)]">{t('admin.settings.process.leadtimeInvalid', { min: LEADTIME_MIN, max: LEADTIME_MAX })}</span>
            )}
          </label>

          <label className="flex flex-col gap-1 text-sm">
            <span className="font-medium text-[var(--foreground)]">{t('admin.settings.auditNoteLabel')}</span>
            <textarea
              aria-label={t('admin.settings.auditNoteLabel')}
              className="min-h-16 rounded-[var(--radius-control)] border border-[var(--border)] bg-[var(--background)] px-3 py-2 text-sm"
              onChange={(event) => setAuditNote(event.target.value)}
              value={auditNote}
            />
          </label>

          <Button disabled={!canSave} onClick={handleSave}>{t('common.save')}</Button>
        </CardContent>
      </Card>

      <Card data-testid="settings-customer-language-section">
        <CardHeader>
          <CardTitle>{t('admin.settings.customerLanguage.title')}</CardTitle>
          <CardDescription>{t('admin.settings.customerLanguage.description')}</CardDescription>
        </CardHeader>
      </Card>
    </div>
  );
}
